// Invoices list — Wave D.
//
// Mirrors the legacy `tricivo_system/operations/invoice_management.php` layout
// at functional level: filter bar (status + date range + search), six summary
// tiles (driven from the page rows), table of records, pagination.
//
// Filter notes:
//   - Legacy uses Work Source + Month picker exclusively. The server's
//     listInvoicesQuerySchema does not yet support a source filter (HANDOFF
//     gap), so we offer status + date range + free-text search backed by the
//     real server contract. The Month/Year affordance is implemented as a
//     `<input type="month">` that we expand into date_from / date_to.
//   - Per-row PDF download via the shared PdfActions component.

import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Download,
  Eye,
  FileSearch,
  FileText,
  Pencil,
  Plus,
  Send,
  Trash2,
} from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { FilterBar } from '../../components/ui/FilterBar/FilterBar';
import { StatsStrip } from '../../components/ui/StatsStrip/StatsStrip';
import { Table, type Column, type RowAction } from '../../components/ui/Table/Table';
import { Pagination } from '../../components/ui/Pagination/Pagination';
import { StatusBadge } from '../../components/ui/StatusBadge/StatusBadge';
import { ConfirmModal } from '../../components/ui/ConfirmModal/ConfirmModal';
import { Select } from '../../components/form/Select';
import { DatePicker } from '../../components/form/DatePicker';
import type { BadgeVariant } from '../../components/ui/Badge/Badge';
import {
  useDeleteInvoice,
  useInvoiceList,
  useInvoiceWorkSources,
  useSendInvoice,
} from './hooks/useInvoices';
import { useAuthStore } from '../../store/authStore';
import { useToast } from '../../hooks/useToast';
import { apiClient } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import type { InvoiceStatus, InvoiceSummary } from '../../types/api';
import styles from './InvoicesListPage.module.css';

const STATUS_MAP: Record<InvoiceStatus, BadgeVariant> = {
  draft: 'muted',
  issued: 'info',
  paid: 'success',
  overdue: 'danger',
  cancelled: 'warning',
};

const STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: 'Draft',
  issued: 'Issued',
  paid: 'Paid',
  overdue: 'Overdue',
  cancelled: 'Cancelled',
};

function formatMoney(n: number): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n);
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

async function downloadInvoicePdf(
  encId: string,
  invoiceNumber: string,
  onError: () => void,
) {
  try {
    const res = await apiClient.raw.get<Blob>(endpoints.invoices.pdf(encId), {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoice-${invoiceNumber}.pdf`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  } catch {
    onError();
  }
}

export function InvoicesListPage(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const role = useAuthStore((s) => s.user?.role ?? null);
  const isAdmin = role === 'admin';

  const status = (params.get('status') ?? '') as '' | InvoiceStatus;
  const dateFrom = params.get('date_from') ?? '';
  const dateTo = params.get('date_to') ?? '';
  const search = params.get('q') ?? '';
  const sourceId = params.get('source_id') ?? '';
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setParams(next, { replace: false });
  }

  const sourceIdNum = sourceId === '' ? undefined : Number.parseInt(sourceId, 10);

  const list = useInvoiceList({
    page,
    status: status || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    q: search || undefined,
    source_id:
      sourceIdNum !== undefined && Number.isFinite(sourceIdNum) ? sourceIdNum : undefined,
  });

  const workSourcesQuery = useInvoiceWorkSources();
  const workSourceOptions = useMemo(() => {
    const rows = workSourcesQuery.data?.work_sources ?? [];
    return [
      { value: '', label: 'All sources' },
      ...rows.map((r) => ({ value: String(r.id), label: r.name })),
    ];
  }, [workSourcesQuery.data]);

  const deleteMutation = useDeleteInvoice();
  const sendMutation = useSendInvoice();

  const [deleteTarget, setDeleteTarget] = useState<InvoiceSummary | null>(null);

  const invoices = list.data?.invoices ?? [];
  const pagination = list.data?.pagination;
  const total = pagination?.total ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  const counts = useMemo(() => {
    const c = { records: 0, paid: 0, issued: 0, overdue: 0, draft: 0, value: 0 };
    for (const inv of invoices) {
      c.records += 1;
      c.value += inv.total_amount;
      if (inv.status === 'paid') c.paid += 1;
      if (inv.status === 'issued') c.issued += 1;
      if (inv.status === 'overdue') c.overdue += 1;
      if (inv.status === 'draft') c.draft += 1;
    }
    return c;
  }, [invoices]);

  const columns: Column<InvoiceSummary>[] = useMemo(
    () => [
      {
        key: 'invoice_number',
        header: 'Invoice #',
        render: (_v, row) => (
          <button
            type="button"
            className={styles.refLink}
            onClick={() => navigate(`/collections/invoices/${encodeURIComponent(row.enc_id)}`)}
          >
            {row.invoice_number}
          </button>
        ),
      },
      {
        key: 'customer_name',
        header: 'Customer',
        render: (_v, row) => row.customer_name ?? <span className={styles.cellMuted}>—</span>,
      },
      {
        key: 'issue_date',
        header: 'Issue Date',
        render: (_v, row) => formatDate(row.issue_date),
      },
      {
        key: 'due_date',
        header: 'Due Date',
        render: (_v, row) => formatDate(row.due_date),
      },
      {
        key: 'total_amount',
        header: 'Total',
        align: 'right',
        render: (_v, row) => formatMoney(row.total_amount),
      },
      {
        key: 'amount_paid',
        header: 'Paid',
        align: 'right',
        render: (_v, row) => formatMoney(row.amount_paid),
      },
      {
        key: 'status',
        header: 'Status',
        render: (_v, row) => (
          <StatusBadge
            status={row.status ?? null}
            statusMap={STATUS_MAP}
            formatLabel={(s) => STATUS_LABELS[s] ?? s}
          />
        ),
      },
    ],
    [navigate],
  );

  const rowActions = (row: InvoiceSummary): RowAction<InvoiceSummary>[] => {
    const actions: RowAction<InvoiceSummary>[] = [
      {
        label: 'View',
        icon: Eye,
        onClick: () => navigate(`/collections/invoices/${encodeURIComponent(row.enc_id)}`),
        variant: 'ghost',
      },
      {
        label: 'Download PDF',
        icon: Download,
        onClick: () =>
          void downloadInvoicePdf(row.enc_id, row.invoice_number, () =>
            toast.error('PDF generation failed. Please try again.'),
          ),
        variant: 'ghost',
      },
      {
        label: 'Send',
        icon: Send,
        onClick: () => sendMutation.mutate({ encId: row.enc_id }),
        variant: 'ghost',
      },
    ];
    if (isAdmin) {
      actions.push({
        label: 'Edit',
        icon: Pencil,
        onClick: () => navigate(`/collections/invoices/${encodeURIComponent(row.enc_id)}/edit`),
        variant: 'ghost',
      });
      actions.push({
        label: 'Delete',
        icon: Trash2,
        onClick: () => setDeleteTarget(row),
        variant: 'danger',
      });
    }
    return actions;
  };

  return (
    <div className={styles.page}>
      <PageHeader
        title="Invoice Management"
        subtitle={`${total} record${total === 1 ? '' : 's'}`}
        actions={
          <>
            <Button
              variant="secondary"
              icon={FileSearch}
              onClick={() => navigate('/collections/invoices/compare')}
            >
              Compare invoice
            </Button>
            {isAdmin ? (
              <Button variant="primary" icon={Plus} onClick={() => navigate('/collections/invoices/create')}>
                New Invoice
              </Button>
            ) : null}
          </>
        }
      />

      <StatsStrip
        ariaLabel="Invoice counters"
        tiles={[
          { key: 'page', label: 'This page', value: counts.records },
          { key: 'paid', label: 'Paid', value: counts.paid },
          { key: 'issued', label: 'Issued', value: counts.issued },
          { key: 'overdue', label: 'Overdue', value: counts.overdue },
          { key: 'draft', label: 'Draft', value: counts.draft },
          { key: 'value', label: 'Total value', value: formatMoney(counts.value) },
        ]}
      />

      <Card flush>
        <FilterBar
          search={{
            value: search,
            onChange: (v) => setParam('q', v),
            placeholder: 'Search by invoice number or customer…',
            ariaLabel: 'Search invoices',
          }}
        >
          <Select
            aria-label="Work source filter"
            value={sourceId}
            onChange={(e) => setParam('source_id', e.target.value)}
            options={workSourceOptions}
          />
          <DatePicker
            aria-label="Date from"
            value={dateFrom}
            onChange={(e) => setParam('date_from', e.target.value)}
          />
          <DatePicker
            aria-label="Date to"
            value={dateTo}
            onChange={(e) => setParam('date_to', e.target.value)}
          />
          <Select
            aria-label="Status filter"
            value={status}
            onChange={(e) => setParam('status', e.target.value)}
            options={[
              { value: '', label: 'All statuses' },
              { value: 'draft', label: 'Draft' },
              { value: 'issued', label: 'Issued' },
              { value: 'paid', label: 'Paid' },
              { value: 'overdue', label: 'Overdue' },
              { value: 'cancelled', label: 'Cancelled' },
            ]}
          />
        </FilterBar>

        <Table
          columns={columns}
          data={invoices}
          keyField={(row) => row.enc_id}
          loading={list.isLoading}
          emptyState={
            <div className={styles.empty}>
              <FileText size={28} aria-hidden />
              <h3>No invoices found</h3>
              <p>Adjust your filters or create a new invoice.</p>
            </div>
          }
          rowActions={rowActions}
          ariaLabel="Invoices"
        />

        {totalPages > 1 ? (
          <div className={styles.paginationWrap}>
            <Pagination
              pagination={{ page, limit: pagination?.limit ?? 50, total, totalPages }}
              onPageChange={(next) => setParam('page', String(next))}
            />
          </div>
        ) : null}
      </Card>

      <ConfirmModal
        open={deleteTarget !== null}
        title="Delete invoice?"
        message={
          deleteTarget !== null
            ? `Delete invoice ${deleteTarget.invoice_number}? This cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={deleteMutation.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget === null) return;
          deleteMutation.mutate(deleteTarget.enc_id, {
            onSuccess: () => {
              setDeleteTarget(null);
              void list.refetch();
            },
          });
        }}
      />
    </div>
  );
}
